import type { SegmentPerson } from "#shared/segmentsTypes.ts";
import { ComboBox, ComboBoxContent, ComboBoxItem } from "@/components/ui/combo-box.tsx";
import { Input, InputGroup } from "@/components/ui/input.tsx";
import { ProgressCircle } from "@/components/ui/progress-circle.tsx";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { useI18nextInstance } from "@squide/i18next";
import { useCallback, useMemo, useState } from "react";
import { Collection, ListBoxLoadMoreItem, type Key } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { PersonAvatar } from "@packages/core-client/people/PersonAvatar.tsx";
import { useDebouncedValue } from "@packages/core-client/hooks/useDebouncedValue.ts";
import { useInfiniteCollection } from "@packages/core-client/collection/useInfiniteCollection.ts";
import { useSession } from "@packages/core-client/session/SessionContext.tsx";
import { segmentsI18NextInstanceKey } from "../../i18n/i18next.ts";
import { useSegmentsClient } from "../../api/segmentsQuery.ts";

const SEARCH_DEBOUNCE_MS = 250;

const NO_CLIENT_FILTER = () => true;

const NO_PLACEHOLDER: SegmentPerson[] = [];

export interface MemberSearchFieldProps {
    addedIds: string[];
    onAdd: (person: SegmentPerson) => void;
}

export function MemberSearchField({ addedIds, onAdd }: MemberSearchFieldProps) {
    const i18nextInstance = useI18nextInstance(segmentsI18NextInstanceKey);
    const { t } = useTranslation("segmentMembers", { i18n: i18nextInstance });
    const { client, queryUtils } = useSegmentsClient();
    const session = useSession();
    const orgId = session?.organizationId ?? "";

    const [query, setQuery] = useState("");
    const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);
    const effectiveQuery = query === "" ? "" : debouncedQuery;

    const { items, isLoading, isRefreshing, isColdError, hasNextPage, isFetchingNextPage, loadMore, loadedAnnouncement } = useInfiniteCollection<SegmentPerson>({
        queryKey: [...queryUtils.searchPeople.key(), { orgId, q: effectiveQuery }],
        queryFn: continuationToken => client.searchPeople({ orgId, q: effectiveQuery || undefined, continuationToken }),
        placeholder: NO_PLACEHOLDER,
        hasActiveFilters: effectiveQuery.trim() !== "",
        getLoadedMoreMessage: count => t("search.liveRegion.loadedMore", { count }),
        onError: "inline"
    });

    const addedIdSet = useMemo(() => new Set(addedIds), [addedIds]);
    const visibleItems = useMemo(() => (isRefreshing ? [] : items.filter(person => !addedIdSet.has(person.id))), [addedIdSet, isRefreshing, items]);

    const handleSelectionChange = useCallback(
        (key: Key | null) => {
            const person = visibleItems.find(candidate => candidate.id === key);

            if (person) {
                onAdd(person);
                setQuery("");
            }
        },
        [visibleItems, onAdd]
    );

    const isSettling = isRefreshing || isLoading;

    const emptyState = () => {
        if (isColdError) {
            return <span className="text-danger text-sm">{t("search.error")}</span>;
        }
        if (isSettling || hasNextPage) {
            return <span className="text-muted-fg text-sm">{t("search.loading")}</span>;
        }

        return <span className="text-muted-fg text-sm">{t("search.noResults", { term: query })}</span>;
    };

    return (
        <>
            <ComboBox aria-label={t("search.label")} items={visibleItems} inputValue={query} onInputChange={setQuery} selectedKey={null} onSelectionChange={handleSelectionChange} defaultFilter={NO_CLIENT_FILTER} allowsEmptyCollection menuTrigger="focus" className="[&_input]:border-border">
                {/* intent-ui-deviation: the input is composed rather than `ComboBoxInput` — the design leads with a
                    magnifier, and ComboBoxInput's trailing chevron reads as "pick from a list", not "search". */}
                <InputGroup>
                    <MagnifyingGlassIcon data-slot="icon" aria-hidden="true" />
                    <Input placeholder={t("search.placeholder")} />
                </InputGroup>
                <ComboBoxContent renderEmptyState={emptyState} className="max-h-64 p-2">
                    <Collection items={visibleItems}>
                        {(person: SegmentPerson) => (
                            <ComboBoxItem id={person.id} textValue={person.displayName} className="flex items-center gap-3 py-2">
                                <PersonAvatar person={person} size="md" />
                                <span className="flex min-w-0 flex-col">
                                    <span className="truncate text-sm font-medium">{person.displayName}</span>
                                    {person.jobTitle && <span className="text-muted-fg truncate text-xs">{person.jobTitle}</span>}
                                </span>
                            </ComboBoxItem>
                        )}
                    </Collection>
                    {!isSettling && hasNextPage && (
                        <ListBoxLoadMoreItem onLoadMore={loadMore} isLoading={isFetchingNextPage}>
                            <div className="flex items-center justify-center gap-2 py-2 text-sm">
                                <ProgressCircle isIndeterminate aria-label={t("search.loadingMore")} className="text-muted-fg size-4" />
                                <span className="text-muted-fg">{t("search.loadingMore")}</span>
                            </div>
                        </ListBoxLoadMoreItem>
                    )}
                </ComboBoxContent>
            </ComboBox>
            <output className="sr-only">{isColdError ? t("search.error") : isSettling ? t("search.loading") : ""}</output>
            <output className="sr-only">{loadedAnnouncement}</output>
        </>
    );
}
